import { useState, useEffect, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, LogOut, Monitor, RefreshCw, Save, ShieldCheck } from 'lucide-react'
import {
  getSecurityPolicy, saveSecurityPolicy, listMemberSessions, endMemberSession,
  type SecurityPolicy, type SecurityPolicyPatch, type MemberSession,
} from '@/api/enterprise'
import { useAuth } from '@/auth/AuthContext'

/**
 * Organization → Security: the sign-in policy for this organization's Twasta
 * accounts (failed-attempt lockout, password rules, session timeouts) and the
 * members' live sessions. Enterprise admins edit; members read.
 * Backend: /api/enterprise/security-policy + /api/enterprise/sessions
 * (services/security_policy.py).
 */

type Section = keyof SecurityPolicy

interface Field {
  section: Section
  key: string
  label: string
  help: string
  kind: 'number' | 'bool' | 'select'
  options?: { value: string; label: string }[]
  min?: number
  max?: number
  unit?: string
}

const SECTIONS: { id: Section; title: string; blurb: string }[] = [
  {
    id: 'mfa',
    title: 'Two-factor sign-in',
    blurb: 'A 6-digit code from an authenticator app after the password. Members set it up in Settings → Account; once it is required, anyone who has not set it up is asked to before they can sign in (after the grace period).',
  },
  {
    id: 'lockout',
    title: 'Failed sign-in attempts',
    blurb: 'After the free attempts each wrong password adds a growing wait before the next one is checked; enough of them lock the account until its owner uses the emailed unlock link, resets the password, or you unlock it from Team.',
  },
  {
    id: 'password',
    title: 'Password rules',
    blurb: 'Applied whenever a member sets a password: registering, accepting an invitation, resetting or changing it.',
  },
  {
    id: 'sessions',
    title: 'Session timeouts',
    blurb: 'Sign members out automatically. Leave at 0 for no limit.',
  },
]

const FIELDS: Field[] = [
  {
    section: 'mfa', key: 'mode', label: 'Two-factor sign-in', kind: 'select',
    help: 'Members who set it up are always asked for their code.',
    options: [
      { value: 'off', label: 'Off — not offered' },
      { value: 'optional', label: 'Optional — members choose' },
      { value: 'required', label: 'Required for everyone' },
    ],
  },
  { section: 'mfa', key: 'grace_days', label: 'Grace period', help: 'Days a member may still sign in before setting it up, once required. 0 = set it up at the next sign-in.', kind: 'number', min: 0, max: 90, unit: 'days' },
  { section: 'lockout', key: 'free_attempts', label: 'Free attempts', help: 'Wrong passwords allowed before any wait applies.', kind: 'number', min: 1, max: 50 },
  { section: 'lockout', key: 'base_delay_s', label: 'First wait', help: 'Doubles with every further failure.', kind: 'number', min: 0, unit: 'seconds' },
  { section: 'lockout', key: 'max_delay_s', label: 'Longest wait', help: 'The wait never grows past this.', kind: 'number', min: 0, unit: 'seconds' },
  { section: 'lockout', key: 'lock_after', label: 'Lock after', help: 'Consecutive failures that lock the account. 0 never locks.', kind: 'number', min: 0, unit: 'failures' },
  { section: 'lockout', key: 'reset_after_s', label: 'Forget failures after', help: 'A streak this old starts again from zero.', kind: 'number', min: 0, unit: 'seconds' },
  { section: 'password', key: 'min_length', label: 'Minimum length', help: 'At least 8.', kind: 'number', min: 8, max: 128, unit: 'characters' },
  { section: 'password', key: 'require_classes', label: 'Character types', help: 'How many of: lowercase, uppercase, digit, symbol.', kind: 'number', min: 0, max: 4 },
  { section: 'password', key: 'breached_check', label: 'Refuse common and breached passwords', help: 'Checked offline — the password never leaves this server.', kind: 'bool' },
  { section: 'password', key: 'history', label: 'Remember previous passwords', help: 'A member cannot reuse their last N. 0 = off.', kind: 'number', min: 0, max: 24 },
  { section: 'password', key: 'max_age_days', label: 'Maximum password age', help: 'Ask for a new password after this many days. 0 = never.', kind: 'number', min: 0, max: 3650, unit: 'days' },
  { section: 'sessions', key: 'idle_minutes', label: 'Idle timeout', help: 'Without activity for this long. Checked about every 5 minutes.', kind: 'number', min: 0, max: 43200, unit: 'minutes' },
  { section: 'sessions', key: 'absolute_hours', label: 'Maximum session length', help: 'Hours after signing in, active or not.', kind: 'number', min: 0, max: 2160, unit: 'hours' },
]

function readField(p: SecurityPolicy, f: Field): number | boolean | string {
  return (p[f.section] as unknown as Record<string, number | boolean | string>)[f.key]
}

function fmt(ts: string | null) {
  if (!ts) return '—'
  return new Date(ts + (ts.endsWith('Z') ? '' : 'Z')).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function deviceLabel(ua: string | null) {
  if (!ua) return 'Unknown device'
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser'
  // Phones first: an iPhone's user agent also says "like Mac OS X", and an
  // Android one says "Linux".
  const os = /iPhone|iPad/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android'
    : /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${browser} on ${os}` : browser
}

export default function SecurityPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'ENTERPRISE_ADMIN'

  const [saved, setSaved] = useState<SecurityPolicy | null>(null)
  const [draft, setDraft] = useState<SecurityPolicy | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [sessions, setSessions] = useState<MemberSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [endingId, setEndingId] = useState<number | null>(null)

  const loadPolicy = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getSecurityPolicy()
      setSaved(res.policy)
      setDraft(res.policy)
    } catch {
      toast.error('Could not load the security policy')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadSessions = useCallback(async () => {
    if (!isAdmin) return
    setSessionsLoading(true)
    try {
      const res = await listMemberSessions()
      setSessions(res.sessions || [])
    } catch {
      toast.error('Could not load sessions')
    } finally {
      setSessionsLoading(false)
    }
  }, [isAdmin])

  useEffect(() => { loadPolicy() }, [loadPolicy])
  useEffect(() => { loadSessions() }, [loadSessions])

  // Only what changed is sent; the server merges it into the stored policy.
  const patch = useMemo<SecurityPolicyPatch>(() => {
    if (!saved || !draft) return {}
    const out: Record<string, Record<string, number | boolean | string>> = {}
    for (const f of FIELDS) {
      const a = readField(saved, f)
      const b = readField(draft, f)
      if (a !== b) {
        out[f.section] = { ...(out[f.section] || {}), [f.key]: b }
      }
    }
    return out as SecurityPolicyPatch
  }, [saved, draft])
  const dirty = Object.keys(patch).length > 0

  const setField = (f: Field, value: number | boolean | string) => {
    setDraft((d) => d && ({ ...d, [f.section]: { ...d[f.section], [f.key]: value } }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await saveSecurityPolicy(patch)
      setSaved(res.policy)
      setDraft(res.policy)
      toast.success('Security policy saved')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Could not save the policy')
    } finally {
      setSaving(false)
    }
  }

  const handleEnd = async (s: MemberSession) => {
    if (s.is_current) return
    if (!window.confirm(`Sign ${s.user_name} out of this session now?`)) return
    setEndingId(s.id)
    try {
      await endMemberSession(s.id)
      toast.success(`${s.user_name} signed out`)
      setSessions((prev) => prev.filter((x) => x.id !== s.id))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || 'Could not end the session')
    } finally {
      setEndingId(null)
    }
  }

  if (loading || !draft) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '4rem' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)' }} />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            className="btn btn-ghost btn-icon"
            onClick={() => navigate(-1)}
            title="Go back"
            style={{ width: 32, height: 32, flexShrink: 0 }}
          >
            <ArrowLeft size={18} />
          </button>
          <ShieldCheck size={24} style={{ color: 'var(--color-accent)' }} />
          <div>
            <h2 style={{ fontSize: '1.5625rem', fontWeight: 600 }}>Sign-in security</h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: 0 }}>
              {isAdmin
                ? 'How your organization’s members sign in to Twasta'
                : 'Your organization’s sign-in rules (set by your enterprise admin)'}
            </p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {dirty && (
              <button className="btn btn-ghost" onClick={() => setDraft(saved)} disabled={saving}>
                Discard
              </button>
            )}
            <button className="btn btn-primary" onClick={handleSave} disabled={!dirty || saving}>
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {SECTIONS.map((sec) => (
        <div key={sec.id} className="card mb-6" style={{ padding: 0 }}>
          <div className="card-header" style={{ padding: '0.875rem 1.25rem', margin: 0, borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }}>
            <span className="card-title">{sec.title}</span>
          </div>
          <div style={{ padding: '1rem 1.25rem' }}>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', margin: '0 0 1rem' }}>
              {sec.blurb}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem 1.5rem' }}>
              {FIELDS.filter((f) => f.section === sec.id).map((f) => {
                const value = readField(draft, f)
                const id = `sec-${f.section}-${f.key}`
                if (f.kind === 'select') {
                  return (
                    <div key={id}>
                      <label htmlFor={id}>{f.label}</label>
                      <select id={id} value={String(value)} disabled={!isAdmin}
                        onChange={(e) => setField(f, e.target.value)} style={{ maxWidth: 280 }}>
                        {(f.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem', marginTop: 4 }}>{f.help}</div>
                    </div>
                  )
                }
                if (f.kind === 'bool') {
                  return (
                    <label key={id} htmlFor={id} className="flex items-start gap-2" style={{ cursor: isAdmin ? 'pointer' : 'default' }}>
                      <input
                        id={id}
                        type="checkbox"
                        checked={Boolean(value)}
                        disabled={!isAdmin}
                        onChange={(e) => setField(f, e.target.checked)}
                        style={{ marginTop: 3, width: 'auto' }}
                      />
                      <span>
                        <span style={{ fontWeight: 500 }}>{f.label}</span>
                        <span style={{ display: 'block', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>{f.help}</span>
                      </span>
                    </label>
                  )
                }
                return (
                  <div key={id}>
                    <label htmlFor={id}>{f.label}</label>
                    <div className="flex items-center gap-2">
                      <input
                        id={id}
                        type="number"
                        inputMode="numeric"
                        min={f.min}
                        max={f.max}
                        value={Number(value)}
                        disabled={!isAdmin}
                        onChange={(e) => {
                          const n = Number.parseInt(e.target.value, 10)
                          setField(f, Number.isFinite(n) ? n : 0)
                        }}
                        style={{ maxWidth: 140 }}
                      />
                      {f.unit && <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>{f.unit}</span>}
                    </div>
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem', marginTop: 4 }}>{f.help}</div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      ))}

      {isAdmin && (
        <div className="card mb-6" style={{ padding: 0 }}>
          <div className="card-header flex items-center justify-between" style={{ padding: '0.875rem 1.25rem', margin: 0, borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }}>
            <span className="card-title">Signed-in sessions ({sessions.length})</span>
            <button className="btn btn-ghost btn-sm" onClick={loadSessions} disabled={sessionsLoading} title="Refresh">
              <RefreshCw size={14} style={sessionsLoading ? { animation: 'spin 1s linear infinite' } : undefined} />
              Refresh
            </button>
          </div>
          <div className="table-container" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Device</th>
                  <th>Signed in</th>
                  <th>Last active</th>
                  <th style={{ width: 130 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{s.user_name}</div>
                      <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>{s.email}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2" title={s.device_info || undefined}>
                        <Monitor size={14} style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }} />
                        <span>{deviceLabel(s.device_info)}</span>
                      </div>
                      {s.ip_address && (
                        <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>{s.ip_address}</div>
                      )}
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>{fmt(s.login_time)}</td>
                    <td style={{ fontSize: '0.875rem' }}>{fmt(s.last_seen_at)}</td>
                    <td>
                      {s.is_current ? (
                        <span className="badge badge-success">This session</span>
                      ) : (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleEnd(s)}
                          disabled={endingId === s.id}
                          title="Sign this member out of this session now"
                        >
                          {endingId === s.id
                            ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                            : <LogOut size={14} />}
                          Sign out
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {sessions.length === 0 && !sessionsLoading && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-secondary)' }}>
                      No one is signed in
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
